/* eslint-disable @typescript-eslint/consistent-type-definitions -- Zero mutator/query arg types must be `type` aliases to satisfy the ReadonlyJSONValue constraint; interfaces lack an implicit index signature. */
import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import {
	isEngineeringRecommendation,
	rebaseGuidanceChange,
} from "@productlane/lib/src/agent-recommendations"

import type { MutatorTx } from "../schema"

export type AcceptAgentRecommendationArgs = {
	id: string
	// A reviewer's edit of a guidance change, applied instead of the proposed text.
	guidance?: string
}

export type DismissAgentRecommendationArgs = {
	id: string
	reason: string
}

export type EditGuidanceArgs = {
	id: string
	after: string
}

export type EditGuideArgs = {
	id: string
	need: string
	suggestion: string
}

export const GUIDANCE_CHANGED_ERROR =
	"The workspace guidance changed after this suggestion was made"

async function loadOpenRecommendation(
	tx: MutatorTx,
	id: string,
	authData: ZeroJwtPayload,
) {
	const [rec] = await tx.query.agentRecommendations.where("id", "=", id).run()
	if (!rec || rec.workspaceId !== authData.workspaceId) return null
	if (rec.status !== "OPEN") return null
	return rec
}

export const agentRecommendationMutators = {
	// Docs fixes apply through the proposal mutators first; this closes the row.
	accept: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: AcceptAgentRecommendationArgs
			ctx: ZeroJwtPayload
		}) => {
			const rec = await loadOpenRecommendation(tx, args.id, authData)
			if (!rec) return
			// Accepting one means filing its Linear issue, which only the server can do.
			if (isEngineeringRecommendation(rec.type)) return
			const now = Date.now()

			if (rec.type === "GUIDANCE") {
				const change = rec.payload.guidance
				const next = args.guidance ?? change?.after
				if (!change || next === undefined) return
				const [settings] = await tx.query.aiChatSettings
					.where("workspaceId", "=", authData.workspaceId)
					.run()
				if (!settings) return
				// Replaying the card's line edits keeps lines added since by a person or another card.
				const updated = rebaseGuidanceChange(
					change.before,
					next,
					settings.customInstructions ?? "",
				)
				if (updated === null) throw new Error(GUIDANCE_CHANGED_ERROR)
				await tx.mutate.aiChatSettings.update({
					id: settings.id,
					customInstructions: updated,
					updatedAt: now,
				})
			}

			await tx.mutate.agentRecommendations.update({
				id: rec.id,
				status: "ACCEPTED",
				acceptedAt: now,
				closedByUserId: authData.userId,
				updatedAt: now,
			})
		},
	),

	// The check ran on the old text, so an edit clears it rather than leave a
	// result next to text it never read.
	editGuidance: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: EditGuidanceArgs
			ctx: ZeroJwtPayload
		}) => {
			const rec = await loadOpenRecommendation(tx, args.id, authData)
			const guidance = rec?.payload.guidance
			if (!rec || rec.type !== "GUIDANCE" || !guidance) return
			const after = args.after.trim()
			if (!after || after === guidance.after) return
			await tx.mutate.agentRecommendations.update({
				id: rec.id,
				payload: { ...rec.payload, guidance: { ...guidance, after } },
				rerunAnswered: null,
				rerunTotal: null,
				updatedAt: Date.now(),
			})
		},
	),

	editGuide: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: EditGuideArgs
			ctx: ZeroJwtPayload
		}) => {
			const rec = await loadOpenRecommendation(tx, args.id, authData)
			const guide = rec?.payload.guide
			if (!rec || !guide) return
			await tx.mutate.agentRecommendations.update({
				id: rec.id,
				payload: {
					...rec.payload,
					guide: {
						...guide,
						need: args.need.trim() || guide.need,
						suggestion: args.suggestion.trim() || guide.suggestion,
					},
				},
				updatedAt: Date.now(),
			})
		},
	),

	dismiss: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: DismissAgentRecommendationArgs
			ctx: ZeroJwtPayload
		}) => {
			const rec = await loadOpenRecommendation(tx, args.id, authData)
			if (!rec) return
			await tx.mutate.agentRecommendations.update({
				id: rec.id,
				status: "DISMISSED",
				dismissReason: args.reason.slice(0, 200),
				closedByUserId: authData.userId,
				updatedAt: Date.now(),
			})
		},
	),

	// Undo for a dismiss. An accept already changed docs or guidance, so it has
	// no undo here.
	reopen: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: { id: string }
			ctx: ZeroJwtPayload
		}) => {
			const [rec] = await tx.query.agentRecommendations
				.where("id", "=", args.id)
				.run()
			if (!rec || rec.workspaceId !== authData.workspaceId) return
			if (rec.status !== "DISMISSED") return
			await tx.mutate.agentRecommendations.update({
				id: rec.id,
				status: "OPEN",
				dismissReason: null,
				closedByUserId: null,
				updatedAt: Date.now(),
			})
		},
	),
}
